import React, { useState, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import NavigationBar from '../components/NavigationBar';
import PartsList from '../components/parts/PartsList';
import PartDetail from '../components/parts/PartDetail';
import FeatureInspectionView from '../components/parts/FeatureInspectionView';
import PartMeasurementEntry from '../components/parts/PartMeasurementEntry';
import NewPartDialog from '../components/parts/NewPartDialog';
import { getPart } from '../components/parts/partsApi';
import { apiErrorMessage } from '../api/apiError';

// ---------------------------------------------------------------------------
// PartsPage — TIER-3 Phase 1 (design doc §5/§7). Sibling tab; internal view
// state (list / detail / feature / measure), same pattern MSAStudyPage /
// Type1GRRPage already use for their own sub-views rather than nested router
// routes — no deep-link requirement is named in the doc for Phase 1.
//
// View state machine:
//   list     -> PartsList (search + New Part)
//   detail   -> PartDetail (features, rev-bump/clone/archive)
//   feature  -> FeatureInspectionView (default feature view — inspection-first)
//   measure  -> PartMeasurementEntry (fullscreen GuidedEntry wrapper)
// ---------------------------------------------------------------------------

function resolveDefaultStream(feature) {
    const streams = Array.isArray(feature?.streams) ? feature.streams : [];
    return streams.find((s) => s.process_label === '') || streams[0] || null;
}

function resolveRequiredContext(stream, part) {
    const streamRequired = stream?.meta?._config?.entry_config?.required;
    if (Array.isArray(streamRequired) && streamRequired.length > 0) return streamRequired;
    const partDefault = part?.meta?._config?.entry_config_default?.required;
    return Array.isArray(partDefault) ? partDefault : [];
}

function PartsPage() {
    const [view, setView] = useState('list'); // 'list' | 'detail' | 'feature' | 'measure'
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [selectedStream, setSelectedStream] = useState(null);
    const [newPartOpen, setNewPartOpen] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [refreshToken, setRefreshToken] = useState(0);

    const bumpRefresh = () => setRefreshToken((t) => t + 1);

    const openPartDetail = useCallback(async (partSummary) => {
        setLoadingDetail(true);
        setDetailError(null);
        try {
            const { ok, status, data } = await getPart(partSummary.id);
            if (!ok) {
                setDetailError(apiErrorMessage(status, data, 'Could not load this part.'));
                return;
            }
            const part = data.part || data;
            setSelectedPart(part);
            setView('detail');
        } catch (e) {
            setDetailError('Network error loading this part.');
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    const handleNewPartCreated = (result) => {
        // The composite response embeds exactly one feature/stream so far —
        // shape it the same way getPart's detail response does (features[]
        // each carrying streams[]) so PartDetail can render it without a
        // round-trip fetch.
        const part = {
            ...result.part,
            features: result.feature ? [{ ...result.feature, streams: result.stream ? [result.stream] : [] }] : [],
        };
        setSelectedPart(part);
        setView('detail');
        setNewPartOpen(false);
        bumpRefresh();
    };

    const handleMeasureNow = (result) => {
        setSelectedPart({
            ...result.part,
            features: result.feature ? [{ ...result.feature, streams: result.stream ? [result.stream] : [] }] : [],
        });
        setSelectedFeature(result.feature);
        setSelectedStream(result.stream);
        setNewPartOpen(false);
        setView('measure');
        bumpRefresh();
    };

    const handleOpenFeature = (feature, stream) => {
        setSelectedFeature(feature);
        setSelectedStream(stream || resolveDefaultStream(feature));
        setView('feature');
    };

    const handleRecordMeasurements = (feature, stream) => {
        setSelectedFeature(feature);
        setSelectedStream(stream || resolveDefaultStream(feature));
        setView('measure');
    };

    const handleBackToList = () => {
        setSelectedPart(null);
        setSelectedFeature(null);
        setSelectedStream(null);
        setView('list');
        bumpRefresh();
    };

    const handleRevBumped = () => {
        // Land on the fresh revision — re-fetch it so its (empty) feature/stream
        // copies are populated the same way any other detail load is.
        bumpRefresh();
        if (selectedPart) handleBackToList();
    };

    const handleCloned = () => {
        bumpRefresh();
        if (selectedPart) handleBackToList();
    };

    const handleArchived = () => {
        handleBackToList();
    };

    const handleFeatureCreated = async () => {
        if (selectedPart) await openPartDetail(selectedPart);
    };

    const handleMeasurementEntryClose = async () => {
        if (selectedPart) {
            await openPartDetail(selectedPart);
        }
        setView('feature');
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#121212' }}>
            {view !== 'measure' && <NavigationBar />}

            <Box
                component="main"
                sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                {detailError && <Alert severity="error" onClose={() => setDetailError(null)}>{detailError}</Alert>}

                {loadingDetail ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                ) : (
                    <>
                        {view === 'list' && (
                            <PartsList
                                refreshToken={refreshToken}
                                onNewPart={() => setNewPartOpen(true)}
                                onSelectPart={openPartDetail}
                            />
                        )}

                        {view === 'detail' && selectedPart && (
                            <PartDetail
                                part={selectedPart}
                                onBack={handleBackToList}
                                onOpenFeature={handleOpenFeature}
                                onRevBumped={handleRevBumped}
                                onCloned={handleCloned}
                                onArchived={handleArchived}
                                onFeatureCreated={handleFeatureCreated}
                            />
                        )}

                        {view === 'feature' && selectedFeature && (
                            <FeatureInspectionView
                                part={selectedPart}
                                feature={selectedFeature}
                                stream={selectedStream}
                                onRecordMeasurements={handleRecordMeasurements}
                            />
                        )}
                    </>
                )}
            </Box>

            {view === 'measure' && selectedFeature && (
                <PartMeasurementEntry
                    part={selectedPart}
                    feature={selectedFeature}
                    stream={selectedStream}
                    requiredContext={resolveRequiredContext(selectedStream, selectedPart)}
                    onClose={handleMeasurementEntryClose}
                />
            )}

            <NewPartDialog
                open={newPartOpen}
                onClose={() => setNewPartOpen(false)}
                onCreated={handleNewPartCreated}
                onMeasureNow={handleMeasureNow}
            />
        </Box>
    );
}

export default PartsPage;
